import { mapPermissionWireRowToPermission } from '@/userManagement/application/mappers/permissionMapper';
import type { PermissionSnapshotDetailWire, SnapshotPermissionWire } from '@/userManagement/application/types/SnapshotWire';

import { resolveSavedAtIso, resolveSavedByDisplay } from './snapshotMetadataWire';

function emptySnapshotDetailWire(): PermissionSnapshotDetailWire {
  return { id: 0, label: '', asOfDate: '', userCount: 0, savedBy: 'Unknown', savedAt: '', permissions: [] };
}

export function coerceSnapshotSavedByFromWire(o: Record<string, unknown>): string {
  return resolveSavedByDisplay(o);
}

function parsePositiveInt(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.floor(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function coerceSnapshotPermissionWireArray(raw: unknown): SnapshotPermissionWire[] {
  if (!Array.isArray(raw)) return [];
  const out: SnapshotPermissionWire[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object') continue;
    const p = mapPermissionWireRowToPermission(item as Record<string, unknown>);
    if (p) {
      out.push({
        featureId: p.featureId,
        featureName: p.featureName,
        category: p.category,
        allowRead: p.allowRead,
        allowWrite: p.allowWrite,
        allowDelete: p.allowDelete,
        allowCentralAccess: p.allowCentralAccess,
        allowExcelExport: p.allowExcelExport
      });
    }
  }
  return out;
}

/** Exported for GET /id metadata that may mirror file JSON shape. */
export function normalizeSnapshotDetailWire(raw: unknown): PermissionSnapshotDetailWire {
  if (raw === null || typeof raw !== 'object') {
    return emptySnapshotDetailWire();
  }
  const o = raw as Record<string, unknown>;
  const permsRaw = o.permissions ?? o.Permissions ?? o.snapshotPermissions ?? o.SnapshotPermissions;
  const permissions = coerceSnapshotPermissionWireArray(permsRaw);
  return {
    id: parsePositiveInt(o.id ?? o.Id),
    label: String(o.label ?? o.Label ?? ''),
    asOfDate: String(o.asOfDate ?? o.AsOfDate ?? ''),
    userCount: parsePositiveInt(o.userCount ?? o.UserCount),
    savedBy: resolveSavedByDisplay(o),
    savedAt: resolveSavedAtIso(o),
    permissions
  };
}

function collectApplyDocumentUserRows(root: Record<string, unknown>): unknown[] {
  const entries = root.entries ?? root.Entries;
  if (Array.isArray(entries) && entries.length > 0) return entries;
  const users = root.users ?? root.Users;
  if (Array.isArray(users) && users.length > 0) return users;
  return [];
}

function nestedRecord(row: Record<string, unknown>, ...keys: string[]): Record<string, unknown> | null {
  for (const k of keys) {
    const v = row[k];
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      return v as Record<string, unknown>;
    }
  }
  return null;
}

function crossUserIdFromEntry(row: Record<string, unknown>): string {
  const direct = row.crossUserId ?? row.CrossUserId ?? row.userId ?? row.UserId ?? row.crossUserID ?? row.CrossUserID;
  if (typeof direct === 'string' && direct.trim() !== '') return direct.trim();
  const nested = nestedRecord(row, 'user', 'User', 'profile', 'Profile');
  if (nested) {
    const id = nested.id ?? nested.Id ?? nested.crossUserId ?? nested.CrossUserId;
    if (typeof id === 'string' && id.trim() !== '') return id.trim();
  }
  return '';
}

function usernameFromEntry(row: Record<string, unknown>): string {
  const direct = row.username ?? row.Username ?? row.userName ?? row.UserName ?? row.login ?? row.Login;
  if (typeof direct === 'string' && direct.trim() !== '') return direct.trim();
  const nested = nestedRecord(row, 'user', 'User', 'profile', 'Profile');
  if (nested) {
    const u = nested.username ?? nested.Username ?? nested.userName ?? nested.UserName;
    if (typeof u === 'string' && u.trim() !== '') return u.trim();
  }
  return '';
}

function permissionsFromApplyEntry(row: Record<string, unknown>): SnapshotPermissionWire[] {
  const permsRaw =
    row.permissions ??
    row.Permissions ??
    row.snapshotPermissions ??
    row.SnapshotPermissions ??
    row.effectivePermissions ??
    row.EffectivePermissions;
  return coerceSnapshotPermissionWireArray(permsRaw);
}

export interface SnapshotFilePerUserPermissionIndex {
  byCrossUserId: Record<string, SnapshotPermissionWire[]>;
  byUsernameLower: Record<string, SnapshotPermissionWire[]>;
}

/** Indexes per-user permission rows from apply-document style snapshot JSON (`entries` / `users`). */
export function buildPerUserSnapshotPermissionIndex(parsed: unknown): SnapshotFilePerUserPermissionIndex {
  const byCrossUserId: Record<string, SnapshotPermissionWire[]> = {};
  const byUsernameLower: Record<string, SnapshotPermissionWire[]> = {};
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { byCrossUserId, byUsernameLower };
  }
  const rows = collectApplyDocumentUserRows(parsed as Record<string, unknown>);
  for (const raw of rows) {
    if (raw === null || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    const perms = permissionsFromApplyEntry(row);
    if (perms.length === 0) continue;
    const uid = crossUserIdFromEntry(row);
    const uname = usernameFromEntry(row);
    if (uid !== '') {
      byCrossUserId[uid] = perms;
    }
    const lower = uname.trim().toLowerCase();
    if (lower !== '') {
      byUsernameLower[lower] = perms;
    }
  }
  return { byCrossUserId, byUsernameLower };
}

/** Gzip magic number (RFC 1952). */
export function isGzipSnapshotFilePayload(buf: ArrayBuffer): boolean {
  const u8 = new Uint8Array(buf);
  return u8.byteLength >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
}

export function coerceAxiosSnapshotFileDataToArrayBuffer(data: unknown): ArrayBuffer {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    const v = data;
    return v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength) as ArrayBuffer;
  }
  throw new Error('Snapshot file response must be binary (use responseType: arraybuffer).');
}

async function gunzipArrayBufferToUtf8(buf: ArrayBuffer): Promise<string> {
  const Decomp = (globalThis as unknown as { DecompressionStream?: typeof DecompressionStream }).DecompressionStream;
  if (!Decomp) {
    throw new Error('DecompressionStream is not available in this environment; cannot decode gzip snapshot file.');
  }
  const stream = new Blob([buf]).stream().pipeThrough(new Decomp('gzip'));
  const out = await new Response(stream).arrayBuffer();
  return new TextDecoder('utf-8', { fatal: false }).decode(out);
}

/** Decompresses gzip when present, otherwise treats bytes as UTF-8 JSON text. */
export async function snapshotFileBytesToUtf8JsonText(buf: ArrayBuffer): Promise<string> {
  if (isGzipSnapshotFilePayload(buf)) {
    return gunzipArrayBufferToUtf8(buf);
  }
  return new TextDecoder('utf-8', { fatal: false }).decode(buf);
}

/**
 * Maps decoded JSON from `GET .../permission-snapshots/{id}/file` into {@link PermissionSnapshotDetailWire}.
 * Accepts a full snapshot object, apply-document `entries` / `users`, or a bare permissions array.
 */
export function snapshotFileJsonToDetailWire(parsed: unknown, fallbackId: number): PermissionSnapshotDetailWire {
  if (Array.isArray(parsed)) {
    return normalizeSnapshotDetailWire({ id: fallbackId, permissions: parsed });
  }
  if (parsed !== null && typeof parsed === 'object') {
    const index = buildPerUserSnapshotPermissionIndex(parsed);
    const hasPerUser = Object.keys(index.byCrossUserId).length > 0 || Object.keys(index.byUsernameLower).length > 0;
    const w = normalizeSnapshotDetailWire(parsed);
    const id = w.id > 0 ? w.id : fallbackId;
    const out: PermissionSnapshotDetailWire = {
      ...w,
      id,
      permissions: w.permissions
    };
    if (hasPerUser) {
      out.snapshotPermissionsByCrossUserId = index.byCrossUserId;
      out.snapshotPermissionsByUsernameLower = index.byUsernameLower;
    }
    return out;
  }
  return emptySnapshotDetailWire();
}
